import { Component, Inject, OnInit, signal } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from 'src/app/app.component';
import { ucFirst } from 'src/app/globals';
import { DaypartObj, Vehicle } from 'src/app/interfaces';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export interface dialogData {
  daypart?: DaypartObj;
  id?: string;
  type?: string;
}

@Component({
  selector: 'app-edit-daypart',
  templateUrl: './edit-daypart.component.html',
  styleUrls: ['./edit-daypart.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    MatCheckbox,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatProgressSpinner,
  ],
})
export class EditDaypartComponent implements OnInit {
  loaded = signal(false);
  daypartsForm: UntypedFormGroup;
  organisationId = localStorage.getItem('orgId');

  hours = signal<number[]>([]);
  minutes = signal<any[]>([]);
  isEditing = signal(this.data && this.data.daypart ? true : false);

  days = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
  minuteStep = 5;

  vehicles = signal<Vehicle[]>([]);

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<EditDaypartComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: dialogData,
  ) {}

  async ngOnInit(): Promise<void> {
    const settings = (
      await getDoc(doc(db, `organisations/${this.organisationId}/settings/general`))
    ).data() as any;

    if (!settings.daypartSettings) {
      this.dialogRef.close();
      this.snackBar.open(
        'Er zijn nog geen begin- en eindtijden ingesteld bij de instellingen.',
        'X',
      );
      return;
    }

    const startHour = settings.daypartSettings.startHour;
    const endHour = settings.daypartSettings.endHour;

    const hoursArray: number[] = [];
    for (let i = startHour; i <= endHour; i++) {
      hoursArray.push(i);
    }
    this.hours.set(hoursArray);

    const minutesArray: any[] = [];
    for (let i = 0; i < 60; i += this.minuteStep) {
      minutesArray.push({
        val: i,
        text: `0${i}`.slice(-2),
      });
    }
    this.minutes.set(minutesArray);

    this.daypartsForm = this.fb.group({
      name: [, Validators.required],
      day: [, Validators.required],
      startHour: [this.hours()[0], Validators.required],
      startMin: [0, Validators.required],
      endHour: [this.hours()[this.hours().length - 1], Validators.required],
      endMin: [0, Validators.required],
    });

    if (this.data.type === 'driver') {
      let vehicles = (
        await getDocs(
          query(
            collection(db, `organisations/${this.organisationId}/vehicles`),
            where(`active`, `==`, true),
          ),
        )
      ).docs.map((doc) => {
        const data = doc.data() as Vehicle;
        return { ...data, id: doc.id, name: ucFirst(data?.name ?? '') };
      });
      vehicles = vehicles.sort(
        (a, b) => a.calendarStyle.sortingNumber - b.calendarStyle.sortingNumber,
      );
      this.vehicles.set(vehicles);

      this.daypartsForm.addControl('vehicles', this.fb.control(this.data?.daypart?.vehicles ?? []));
      if (!this.data?.daypart?.vehicles) {
        this.daypartsForm.controls.vehicles.setValue(this.vehicles().map((v) => v.id));
      }
    }

    if (this.isEditing()) {
      // create new obj because data types are interfering with interfaces
      const newDaypartObj = this.data.daypart as any;
      newDaypartObj.day = newDaypartObj.dayString;

      newDaypartObj.startHour = Number(newDaypartObj.startHour);
      newDaypartObj.endHour = Number(newDaypartObj.endHour);
      newDaypartObj.startMin = Number(newDaypartObj.startMin);
      newDaypartObj.endMin = Number(newDaypartObj.endMin);

      this.daypartsForm.patchValue(newDaypartObj);
    }
    this.loaded.set(true);
  }

  async addDaypart() {
    if (this.daypartsForm.valid) {
      if (this.daypartsForm.value.startHour > this.daypartsForm.value.endHour) {
        return this.snackBar.open('De begintijd mag niet later zijn dan de eindtijd.', 'X', {
          duration: 5000,
        });
      }
      const day = this.daypartsForm.value.day;
      let saveObj = { ...this.daypartsForm.value } as any;
      switch (day) {
        case 'Maandag':
          saveObj.day = 0;
          break;
        case 'Dinsdag':
          saveObj.day = 1;
          break;
        case 'Woensdag':
          saveObj.day = 2;
          break;
        case 'Donderdag':
          saveObj.day = 3;
          break;
        case 'Vrijdag':
          saveObj.day = 4;
          break;
        case 'Zaterdag':
          saveObj.day = 5;
          break;
        case 'Zondag':
          saveObj.day = 6;
          break;
      }
      saveObj.name = ucFirst(saveObj?.name ?? '');
      delete saveObj.vehicles;

      if (this.isEditing()) {
        setDoc(
          doc(db, `organisations/${this.organisationId}/dayparts/${this.data.id}`),
          {
            daypart: saveObj,
            vehicles: this.daypartsForm.value.vehicles ?? [],
          },
          { merge: true },
        );
      } else {
        await addDoc(collection(db, `organisations/${this.organisationId}/dayparts`), {
          daypart: saveObj,
          vehicles: this.daypartsForm.value.vehicles ?? [],
          type: this.data.type,
        });
      }
      this.snackBar.open(`Dagdeel opgeslagen.`, 'X', {
        duration: 5000,
      });
      this.dialogRef.close();
    }
  }

  handleVehicleClick(event, value: any) {
    const currentArray = [...(this.daypartsForm.value.vehicles ?? [])];
    if (event.checked) {
      if (currentArray.indexOf(value) === -1) {
        currentArray.push(value);
      }
    } else {
      const index = currentArray.indexOf(value);
      if (index !== -1) {
        currentArray.splice(index, 1);
      }
    }
    this.daypartsForm.controls.vehicles.setValue(currentArray);
  }

  vehicleChecked(vehicleId: any): boolean {
    if (this.daypartsForm.value?.vehicles) {
      return this.daypartsForm.value.vehicles.indexOf(vehicleId) !== -1;
    }
    return false;
  }
}
